import React, { useEffect, useState } from 'react';
import { DoorOpen, Plus, Pencil, Power, X, Check, AlertCircle, Search, Download } from 'lucide-react';
import apiClient from '../../config/api.config';

interface Salle {
  id: number; code: string; nom: string; batiment: string; capacite: number;
  equipements: string[]; etat_manuel: string; etat_motif: string; note: string;
  actif: boolean; etat: string;
}
const vide = (): Partial<Salle> => ({
  code: '', nom: '', batiment: '', capacite: 0, equipements: [], etat_manuel: 'disponible', etat_motif: '', note: '', actif: true,
});

const ETAT_BADGE: Record<string, { label: string; cls: string }> = {
  disponible: { label: 'Disponible', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  maintenance: { label: 'Indisponible', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const Salles: React.FC = () => {
  const [salles, setSalles] = useState<Salle[]>([]);
  const [batiments, setBatiments] = useState<string[]>([]);
  const [filtreBat, setFiltreBat] = useState('');
  const [recherche, setRecherche] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [modal, setModal] = useState<Partial<Salle> | null>(null);
  const [equipTxt, setEquipTxt] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const charger = () => {
    apiClient.get('/vie-scolaire/salles/', { params: showAll ? { actif: 'all' } : {} })
      .then((r) => { setSalles(r.data.results || []); setBatiments(r.data.batiments || []); })
      .catch(() => setMsg({ type: 'error', text: 'Chargement des salles impossible.' }));
  };
  useEffect(charger, [showAll]);

  const ouvrir = (s?: Salle) => {
    setModal(s ? { ...s } : vide());
    setEquipTxt(s ? (s.equipements || []).join(', ') : '');
    setMsg(null);
  };

  const enregistrer = async () => {
    if (!modal) return;
    const payload = {
      ...modal,
      equipements: equipTxt.split(',').map((e) => e.trim()).filter(Boolean),
      capacite: Number(modal.capacite) || 0,
    };
    if (!payload.code?.trim() || !payload.nom?.trim()) {
      setMsg({ type: 'error', text: 'Code et nom sont requis.' }); return;
    }
    setSaving(true);
    try {
      if (modal.id) await apiClient.patch(`/vie-scolaire/salles/${modal.id}/`, payload);
      else await apiClient.post('/vie-scolaire/salles/', payload);
      setModal(null); charger();
      setMsg({ type: 'success', text: 'Salle enregistrée.' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Échec de l’enregistrement.' });
    } finally { setSaving(false); }
  };

  const [importing, setImporting] = useState(false);
  const importerBrassage = async () => {
    if (!window.confirm('Importer les salles déjà saisies dans le module Brassage (examens) ? Les salles existantes ne seront pas dupliquées.')) return;
    setImporting(true); setMsg(null);
    try {
      const r = await apiClient.post('/vie-scolaire/salles/importer-brassage/');
      charger();
      setMsg({ type: 'success', text: `${r.data.importes} salle(s) importée(s) (sur ${r.data.detectes} détectée(s)).` });
    } catch {
      setMsg({ type: 'error', text: 'Import impossible.' });
    } finally { setImporting(false); }
  };

  const basculerActif = async (s: Salle) => {
    try {
      if (s.actif) {
        if (!window.confirm(`Désactiver la salle ${s.code} ?`)) return;
        await apiClient.delete(`/vie-scolaire/salles/${s.id}/`);
      } else {
        await apiClient.patch(`/vie-scolaire/salles/${s.id}/`, { actif: true });
      }
      charger();
    } catch { setMsg({ type: 'error', text: 'Action impossible.' }); }
  };

  const filtrees = salles.filter((s) =>
    (!filtreBat || s.batiment === filtreBat) &&
    (!recherche || `${s.code} ${s.nom} ${s.batiment}`.toLowerCase().includes(recherche.toLowerCase())));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <DoorOpen size={26} /> Salles
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Registre des salles : capacité, équipements fixes, état. Base du planning d'occupation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={importerBrassage} disabled={importing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium rounded-lg disabled:opacity-50">
            <Download size={16} /> {importing ? 'Import…' : 'Importer (brassage)'}
          </button>
          <button onClick={() => ouvrir()} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
            <Plus size={16} /> Nouvelle salle
          </button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${msg.type === 'success'
          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
          : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}`}>
          {msg.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}<span>{msg.text}</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
        </div>
        <select value={filtreBat} onChange={(e) => setFiltreBat(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">Tous les bâtiments</option>
          {batiments.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-blue-600" />
          Inclure désactivées
        </label>
        <span className="text-sm text-gray-400 ml-auto">{filtrees.length} salle(s)</span>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300">
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Nom</th>
              <th className="text-left px-4 py-2">Bâtiment</th>
              <th className="text-center px-4 py-2">Capacité</th>
              <th className="text-left px-4 py-2">Équipements</th>
              <th className="text-center px-4 py-2">État</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtrees.map((s) => (
              <tr key={s.id} className={`border-t border-gray-100 dark:border-gray-700 ${!s.actif ? 'opacity-50' : ''}`}>
                <td className="px-4 py-2 font-mono text-gray-900 dark:text-gray-100">{s.code}</td>
                <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{s.nom}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{s.batiment || '—'}</td>
                <td className="px-4 py-2 text-center">{s.capacite || '—'}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(s.equipements || []).map((e, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{e}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${ETAT_BADGE[s.etat_manuel]?.cls || ''}`}>
                    {ETAT_BADGE[s.etat_manuel]?.label || s.etat_manuel}
                  </span>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => ouvrir(s)} className="p-1.5 text-gray-500 hover:text-blue-600" title="Modifier"><Pencil size={16} /></button>
                  <button onClick={() => basculerActif(s)} className={`p-1.5 ${s.actif ? 'text-gray-500 hover:text-red-600' : 'text-green-600'}`} title={s.actif ? 'Désactiver' : 'Réactiver'}><Power size={16} /></button>
                </td>
              </tr>
            ))}
            {filtrees.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucune salle. Cliquez sur « Nouvelle salle ».</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{modal.id ? 'Modifier la salle' : 'Nouvelle salle'}</h3>
              <button onClick={() => setModal(null)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-gray-500 mb-1">Code *</label>
                  <input value={modal.code || ''} onChange={(e) => setModal({ ...modal, code: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Capacité</label>
                  <input type="number" value={modal.capacite ?? 0} onChange={(e) => setModal({ ...modal, capacite: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
              </div>
              <div><label className="block text-xs text-gray-500 mb-1">Nom *</label>
                <input value={modal.nom || ''} onChange={(e) => setModal({ ...modal, nom: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Bâtiment</label>
                <input value={modal.batiment || ''} onChange={(e) => setModal({ ...modal, batiment: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Équipements fixes (séparés par des virgules)</label>
                <input value={equipTxt} onChange={(e) => setEquipTxt(e.target.value)} placeholder="Projecteur, Tableau, Climatiseur…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs text-gray-500 mb-1">État</label>
                  <select value={modal.etat_manuel || 'disponible'} onChange={(e) => setModal({ ...modal, etat_manuel: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    <option value="disponible">Disponible</option>
                    <option value="maintenance">Indisponible (travaux / maintenance)</option>
                  </select></div>
                <div><label className="block text-xs text-gray-500 mb-1">Motif (si indisponible)</label>
                  <input value={modal.etat_motif || ''} onChange={(e) => setModal({ ...modal, etat_motif: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
              </div>
              <div><label className="block text-xs text-gray-500 mb-1">Note</label>
                <input value={modal.note || ''} onChange={(e) => setModal({ ...modal, note: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200">Annuler</button>
              <button onClick={enregistrer} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg">
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salles;
